import React from 'react';

const LoadingSpinner = ({ 
  size = 'medium', 
  color = 'primary', 
  text = null, 
  fullScreen = false,
  className = '' 
}) => {
  // Size classes
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-8 w-8',
    large: 'h-12 w-12',
    xlarge: 'h-16 w-16'
  };

  // Color classes
  const colorClasses = {
    primary: 'text-blue-600',
    secondary: 'text-gray-600',
    white: 'text-white',
    success: 'text-green-600',
    warning: 'text-yellow-600',
    error: 'text-red-600'
  };

  const spinnerClasses = `animate-spin ${sizeClasses[size]} ${colorClasses[color]} ${className}`;

  const SpinnerSVG = () => (
    <svg 
      className={spinnerClasses}
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24"
    >
      <circle 
        className="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        strokeWidth="4"
      />
      <path 
        className="opacity-75" 
        fill="currentColor" 
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const content = (
    <div className="flex flex-col items-center justify-center space-y-2">
      <SpinnerSVG />
      {text && (
        <p className={`text-sm ${colorClasses[color]} animate-pulse`}>
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 backdrop-blur-sm z-50 flex items-center justify-center">
        {content}
      </div>
    );
  }

  return content;
};

// Preset spinner components
export const SmallSpinner = (props) => (
  <LoadingSpinner size="small" {...props} />
);

export const MediumSpinner = (props) => (
  <LoadingSpinner size="medium" {...props} />
);

export const LargeSpinner = (props) => (
  <LoadingSpinner size="large" {...props} />
);

export const FullScreenSpinner = ({ text = 'Loading...', ...props }) => (
  <LoadingSpinner fullScreen={true} text={text} {...props} />
);

// Button spinner (for loading buttons)
export const ButtonSpinner = ({ className = '', ...props }) => (
  <LoadingSpinner 
    size="small" 
    color="white" 
    className={`mr-2 ${className}`} 
    {...props} 
  />
);

// Page loading spinner
export const PageSpinner = ({ text = 'Loading page...', ...props }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <LoadingSpinner 
      size="large" 
      text={text} 
      {...props} 
    />
  </div>
);

// Card loading spinner
export const CardSpinner = ({ text = 'Loading...', ...props }) => (
  <div className="flex items-center justify-center p-8">
    <LoadingSpinner 
      size="medium" 
      text={text} 
      {...props} 
    />
  </div>
);

// Inline spinner (for inline loading states)
export const InlineSpinner = (props) => (
  <LoadingSpinner 
    size="small" 
    className="inline-block" 
    {...props} 
  />
);

// Dots loading animation (alternative style)
export const DotsSpinner = ({ 
  size = 'medium', 
  color = 'primary', 
  className = '' 
}) => {
  const sizeClasses = {
    small: 'h-1 w-1',
    medium: 'h-2 w-2',
    large: 'h-3 w-3'
  };

  const colorClasses = {
    primary: 'bg-blue-600',
    secondary: 'bg-gray-600',
    white: 'bg-white',
    success: 'bg-green-600',
    warning: 'bg-yellow-600',
    error: 'bg-red-600'
  };

  const dotClasses = `${sizeClasses[size]} ${colorClasses[color]} rounded-full`;

  return (
    <div className={`flex space-x-1 ${className}`}>
      <div className={`${dotClasses} animate-bounce`} style={{ animationDelay: '0ms' }} />
      <div className={`${dotClasses} animate-bounce`} style={{ animationDelay: '150ms' }} />
      <div className={`${dotClasses} animate-bounce`} style={{ animationDelay: '300ms' }} />
    </div>
  );
};

// Pulse loading animation (for skeleton screens)
export const PulseSpinner = ({ 
  width = 'w-full', 
  height = 'h-4', 
  className = '' 
}) => (
  <div className={`${width} ${height} bg-gray-200 dark:bg-gray-700 rounded animate-pulse ${className}`} />
);

export default LoadingSpinner;